/* Motivo custom sections — small, dependency-free behaviors. Loaded with defer. */
(function () {
  if (window.__motivoSections) return;
  window.__motivoSections = true;

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* Before / after comparison slider */
  class MvBeforeAfter extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.handle = this.querySelector('.mv-ba__handle');
      this.pointerId = null;
      this.set(parseFloat(this.dataset.start || '50'));

      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.pointerId = e.pointerId;
        this.setPointerCapture(e.pointerId);
        this.classList.add('is-dragging');
        this.set(this.fromEvent(e));
      });
      this.addEventListener('pointermove', (e) => {
        if (e.pointerId === this.pointerId) this.set(this.fromEvent(e));
      });
      const end = (e) => {
        if (e.pointerId !== this.pointerId) return;
        this.pointerId = null;
        this.classList.remove('is-dragging');
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      if (this.handle) {
        this.handle.addEventListener('keydown', (e) => {
          let p = this.pos;
          if (e.key === 'ArrowLeft') p -= 5;
          else if (e.key === 'ArrowRight') p += 5;
          else if (e.key === 'Home') p = 0;
          else if (e.key === 'End') p = 100;
          else return;
          e.preventDefault();
          this.set(p);
        });
      }
    }

    fromEvent(e) {
      const r = this.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    }

    set(p) {
      this.pos = Math.max(0, Math.min(100, p));
      this.style.setProperty('--pos', this.pos + '%');
      if (this.handle) this.handle.setAttribute('aria-valuenow', Math.round(this.pos));
    }
  }

  /* Feature callouts: mobile swipe nav, active dot, desktop connector lines */
  class MvFeature extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.dots = Array.from(this.querySelectorAll('.mv-feat__dot'));
      this.svg = this.querySelector('.mv-feat__lines');
      this.track = this.querySelector('.mv-feat__track');

      if (this.track) {
        this.slides = Array.from(this.track.children);
        this.prev = this.querySelector('[data-mv-prev]');
        this.next = this.querySelector('[data-mv-next]');
        const go = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
        if (this.prev) this.prev.addEventListener('click', () => go(-1));
        if (this.next) this.next.addEventListener('click', () => go(1));
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => this.update());
        }, { passive: true });
        this.update();
      }

      if (this.svg && 'ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.draw());
        this._ro.observe(this);
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', () => this.draw(), { once: true });
        });
      }
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
    }

    update() {
      const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.slides.forEach((s, j) => s.classList.toggle('is-active', j === i));
      const id = this.slides[i] && this.slides[i].dataset.id;
      this.dots.forEach((d) => d.classList.toggle('is-active', d.dataset.id === id));
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= this.slides.length - 1;
    }

    draw() {
      if (!this.svg || getComputedStyle(this.svg).display === 'none') return;
      const media = this.svg.parentElement.getBoundingClientRect();
      let d = '';
      this.querySelectorAll('.mv-feat__col').forEach((col) => {
        const leftSide = col.dataset.side === 'first';
        col.querySelectorAll('.mv-feat__item').forEach((item) => {
          const dot = this.querySelector('.mv-feat__dot[data-id="' + item.dataset.id + '"]');
          const icon = item.querySelector('.mv-feat__icon');
          if (!dot || !icon) return;
          const a = icon.getBoundingClientRect();
          const b = dot.getBoundingClientRect();
          const x1 = (leftSide ? a.right : a.left) - media.left;
          const y1 = a.top + a.height / 2 - media.top;
          const x2 = b.left + b.width / 2 - media.left;
          const y2 = b.top + b.height / 2 - media.top;
          d += 'M' + x1.toFixed(1) + ' ' + y1.toFixed(1) + 'L' + x2.toFixed(1) + ' ' + y2.toFixed(1);
        });
      });
      this.svg.innerHTML = d ? '<path d="' + d + '"></path>' : '';
    }
  }

  /* Testimonials: highlight the centered card on mobile, read more, optional arrows */
  class MvTestimonials extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('.mv-tst__track');
      if (!this.track) return;
      this.cards = Array.from(this.track.children);

      this.cards.forEach((card) => {
        const text = card.querySelector('.mv-tst__text');
        const btn = card.querySelector('.mv-tst__more');
        if (!text || !btn) return;
        requestAnimationFrame(() => {
          if (text.scrollHeight > text.clientHeight + 2) btn.classList.add('is-shown');
        });
        btn.addEventListener('click', () => {
          const open = text.classList.toggle('is-clamped') === false;
          btn.textContent = open ? btn.dataset.less : btn.dataset.more;
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });

      const prev = this.querySelector('[data-mv-prev]');
      const next = this.querySelector('[data-mv-next]');
      const step = () => (this.cards[0] ? this.cards[0].getBoundingClientRect().width + 20 : this.track.clientWidth);
      if (prev) prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
      }, { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.update();
    }

    update() {
      const box = this.track.getBoundingClientRect();
      const center = box.left + box.width / 2;
      let best = null;
      let bestDist = Infinity;
      this.cards.forEach((c) => {
        const r = c.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - center);
        if (d < bestDist) { bestDist = d; best = c; }
      });
      this.cards.forEach((c) => c.classList.toggle('is-active', c === best));
    }
  }

  /* FAQ tabs (questions use native <details>) */
  class MvFaq extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      const select = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          let j = i;
          if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
          else return;
          e.preventDefault();
          tabs[j].focus();
          select(tabs[j]);
        });
      });
    }
  }

  /* Generic swipe row with prev/next arrows: [data-mv-track] + [data-mv-prev]/[data-mv-next] */
  class MvCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-mv-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-mv-prev]');
      this.next = this.querySelector('[data-mv-next]');
      const step = () => {
        const first = this.track.firstElementChild;
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
      }, { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.update();
    }

    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const scrollable = max > 2;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prev) this.prev.disabled = !scrollable || t.scrollLeft <= 2;
      if (this.next) this.next.disabled = !scrollable || t.scrollLeft >= max - 2;
    }
  }

  /* YouTube facade: loads the player only after a click */
  class MvVideo extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const btn = this.querySelector('.mv-video__play');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(this.dataset.id) + '?autoplay=1&rel=0&playsinline=1';
        f.title = this.dataset.title || 'Video';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        f.allowFullscreen = true;
        this.replaceChildren(f);
      }, { once: true });
    }
  }

  define('mv-carousel', MvCarousel);
  define('mv-video', MvVideo);
  define('mv-before-after', MvBeforeAfter);
  define('mv-feature', MvFeature);
  define('mv-testimonials', MvTestimonials);
  define('mv-faq', MvFaq);
})();
